"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  CheckboxRow,
  ResultBanner,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
  type Verdict,
} from "@/components/tools/ui";

/* Work at Height Regulations 2005 assessment helper.
   Structure follows the WAHR hierarchy (Reg. 6): avoid -> prevent (collective
   first, then personal) -> minimise, plus the mandatory rescue plan (Reg. 4(1)(c))
   for any work relying on a harness or a MEWP. Grounded against the RamsDocs
   knowledge bank and HSE guidance (assess.htm, mewp.htm, INDG401/INDG455,
   PASMA, SG4:22, HSG33). This is a planning aid, not approval to proceed. */

type AccessMethod =
  | "stepladder"
  | "leaning-ladder"
  | "podium"
  | "tower"
  | "mewp"
  | "scaffold"
  | "roof-flat"
  | "roof-pitched";

interface MethodDef {
  label: string;
  /** True when work on this method relies on a harness / can strand a worker,
      so a self-sufficient rescue plan is mandatory and cannot be skipped. */
  rescueMandatory: boolean;
  /** Tailored, safety-critical prompts for the method. */
  prompts: Prompt[];
}

interface Prompt {
  id: string;
  label: string;
}

const METHODS: Record<AccessMethod, MethodDef> = {
  stepladder: {
    label: "Stepladder",
    rescueMandatory: false,
    prompts: [
      { id: "duration", label: "Work is short-duration (under ~30 minutes in one position) and low-risk, with no reasonable platform alternative — justification recorded" },
      { id: "class", label: "EN 131 Professional (Class 1) stepladder, inspected (stiles, treads, feet, locking spreaders) before use" },
      { id: "open", label: "Stepladder fully opened with the spreader locked, on firm, level, non-slippery ground" },
      { id: "contact", label: "Three points of contact maintained; not working from the top two steps without a suitable handhold" },
      { id: "sideload", label: "No side-loading tasks (e.g. drilling into masonry) carried out from the steps" },
    ],
  },
  "leaning-ladder": {
    label: "Leaning ladder",
    rescueMandatory: false,
    prompts: [
      { id: "duration", label: "Work is short-duration (under ~30 minutes in one position) and low-risk — ladder justified against the WAHR risk assessment, not used as a default" },
      { id: "class", label: "EN 131 Professional (Class 1) ladder of correct length, inspected (stiles, rungs, feet) before use" },
      { id: "angle", label: "Set at the 75° (1 in 4) angle on firm, level, non-slippery ground" },
      { id: "secured", label: "Tied at the stiles or a stability device used; footed by a second person only where securing is not reasonably practicable" },
      { id: "contact", label: "Three points of contact kept; belt buckle (navel) kept within the stiles — no overreaching" },
    ],
  },
  podium: {
    label: "Podium steps",
    rescueMandatory: false,
    prompts: [
      { id: "guardrails", label: "Self-closing gate and full guardrails fitted and used; operative works inside the platform" },
      { id: "brakes", label: "Castor brakes locked before climbing; platform not moved while occupied" },
      { id: "ground", label: "Firm, level ground checked; within the manufacturer's safe working load" },
      { id: "inspect", label: "Podium inspected (frame, gate, brakes, platform) before use" },
    ],
  },
  tower: {
    label: "Mobile access tower",
    rescueMandatory: false,
    prompts: [
      { id: "pasma", label: "Assembled, altered and dismantled only by PASMA-trained operatives (in-date card)" },
      { id: "build", label: "Built using the 3T (through-the-trap) or advance-guardrail (AGR) method — no one ever on an unguarded platform" },
      { id: "stabilisers", label: "Stabilisers/outriggers fitted per the manual; within the maximum height-to-base ratio" },
      { id: "ground", label: "Firm, level ground able to take the load; castor brakes locked before climbing; cleared before any reposition" },
      { id: "tag", label: "Inspected and tagged (Scafftag) after assembly, after any move/alteration, and at least every 7 days" },
    ],
  },
  mewp: {
    label: "MEWP (boom / scissor)",
    rescueMandatory: true,
    prompts: [
      { id: "ipaf", label: "IPAF-certified operator (3a vertical / 3b boom) familiarised on the specific machine" },
      { id: "ground", label: "Ground bearing capacity, gradient, voids and underground services assessed; outriggers on spreader pads where fitted" },
      { id: "harness", label: "On a boom MEWP, full-body harness with a restraint lanyard clipped to the designated basket anchor (scissor lifts: guardrails are the primary protection)" },
      { id: "loler", label: "LOLER thorough examination certificate in date (within 6 months) and daily pre-use inspection/function test done" },
      { id: "exclusion", label: "Exclusion zone set; overhead lines/structures surveyed and crushing/entrapment points kept clear" },
    ],
  },
  scaffold: {
    label: "Tube & fitting / system scaffold",
    rescueMandatory: false,
    prompts: [
      { id: "erector", label: "Erected, altered and struck only by competent (CISRS-carded) scaffolders" },
      { id: "design", label: "TG20:21 compliant or a bespoke design with a competent design check; ground bearing / sole boards confirmed" },
      { id: "handover", label: "Handover certificate and Scafftag completed before following trades use it; re-tagged after any alteration" },
      { id: "inspection", label: "Inspected before first use, after any event affecting stability, and at least every 7 days" },
      { id: "fallingobjects", label: "Toe boards, brick guards and/or netting fitted; exclusion zone beneath the erection face" },
    ],
  },
  "roof-flat": {
    label: "Roof work (flat)",
    rescueMandatory: false,
    prompts: [
      { id: "edge", label: "Permanent or temporary edge protection (double guardrail + toe board) at every open edge, including rooflights/openings" },
      { id: "fragile", label: "Rooflights and any suspect areas treated as fragile until proven otherwise — covered or guarded" },
      { id: "access", label: "Safe access onto the roof (e.g. scaffold stair/tower), and a managed materials route — debris not dropped over the edge" },
      { id: "weather", label: "Wind/weather stop limit set; surface checked for ice, wet or moss before and during work" },
    ],
  },
  "roof-pitched": {
    label: "Roof work (pitched / fragile)",
    rescueMandatory: false,
    prompts: [
      { id: "fragile", label: "Whole roof deemed fragile until a competent survey / ACR(M)001 evidence proves otherwise; fragile sheets and rooflights marked" },
      { id: "below", label: "Access from below (MEWP / independent scaffold) considered first to avoid going onto the roof where reasonably practicable" },
      { id: "boards", label: "Staging or crawling boards spanning the load-bearing purlins; never stepping onto sheets between boards or onto a rooflight" },
      { id: "nets", label: "Fall-arrest netting beneath the work area and/or rooflight covers fitted before anyone goes near the roof" },
      { id: "edge", label: "Edge protection at eaves, verges and valleys; materials route (chute/hoist) so debris does not travel via the edge by hand" },
      { id: "weather", label: "No work in high winds; surface checked for ice, frost, wet or moss" },
    ],
  },
};

const STEP1: Prompt[] = [
  { id: "avoid", label: "Can the work at height be avoided altogether (e.g. assembled at ground level, telescopic/extension tools, redesign)?" },
  { id: "safeplace", label: "If it cannot be avoided, can it be done from an existing safe place (a permanent platform, edge-protected roof, or through an opening) without using temporary access?" },
];

const STEP3_COLLECTIVE: Prompt[] = [
  { id: "collective-first", label: "Collective fall PREVENTION (guardrails / working platforms) provided before any reliance on personal protection (harnesses)" },
  { id: "collective-mitigation", label: "Where falls cannot be prevented, collective MITIGATION (safety nets / soft-landing) considered before personal fall arrest" },
];

const STEP3_OBJECTS: Prompt[] = [
  { id: "toeboards", label: "Toe boards / brick guards fitted to working platforms" },
  { id: "exclusion", label: "Exclusion zone barriered beneath the work; public and other trades kept clear" },
  { id: "tethers", label: "Hand tools tethered or contained; materials lowered, never dropped or thrown" },
];

const STEP3_WEATHER: Prompt[] = [
  { id: "windlimit", label: "Wind-speed stop limit set for the access equipment in use; forecast monitored" },
  { id: "surface", label: "Work stood down in ice, frost, heavy rain or other conditions that compromise grip or stability" },
];

const STEP4_RESCUE: Prompt[] = [
  { id: "who", label: "Named, on-site person(s) responsible for recovering a suspended or stranded worker — trained and available for the whole shift" },
  { id: "equipment", label: "Rescue equipment identified and on site (e.g. descender/recovery kit; for a MEWP, a ground person who can operate the auxiliary/emergency lowering controls)" },
  { id: "trauma", label: "Maximum suspension time accounted for — suspension trauma can incapacitate a hanging worker within minutes; trauma-relief straps fitted where a harness is used" },
  { id: "no999", label: "Plan does NOT rely on a 999 call — emergency-service rescue from height takes too long; recovery is self-sufficient" },
];

function emptyChecks(): Record<string, boolean> {
  return {};
}

export function WorkAtHeightAssessment() {
  // Header
  const [task, setTask] = useState("");
  const [location, setLocation] = useState("");
  const [duration, setDuration] = useState("");
  const [frequency, setFrequency] = useState("");
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();

  // Step 1 — hierarchy gate
  const [avoid, setAvoid] = useState<"" | "yes" | "no">("");
  const [safePlace, setSafePlace] = useState<"" | "yes" | "no">("");
  // Justification for dropping to a lower tier of the hierarchy (using access
  // equipment / fall arrest rather than avoiding the work or working from an
  // existing safe place). WAHR 2005 expects this reasoning to be recorded.
  const [hierarchyJustification, setHierarchyJustification] = useState("");

  // Step 2 — access method + tailored checklist
  const [method, setMethod] = useState<AccessMethod | "">("");
  const [methodChecks, setMethodChecks] = useState<Record<string, boolean>>(emptyChecks);
  // Free-text "short duration, low risk" justification, required when a ladder
  // is the chosen access method (WAHR 2005 — ladders are not a default).
  const [ladderJustification, setLadderJustification] = useState("");

  // Step 3 + 4
  const [protectionChecks, setProtectionChecks] = useState<Record<string, boolean>>(emptyChecks);
  // Whether personal fall arrest / a harness is relied on for this task. When
  // true a self-sufficient rescue plan is mandatory even if the access method
  // itself does not force it (e.g. harness-based roof work).
  const [harnessUsed, setHarnessUsed] = useState(false);
  const [rescueChecks, setRescueChecks] = useState<Record<string, boolean>>(emptyChecks);
  const [rescueNotes, setRescueNotes] = useState("");
  // Wind / weather stop-work threshold for the equipment in use.
  const [windThreshold, setWindThreshold] = useState("");

  const resultTracked = useRef(false);

  const methodDef = method ? METHODS[method] : null;
  const isLadder = method === "stepladder" || method === "leaning-ladder";
  // A rescue plan is mandatory when the method forces it (MEWP) or when the
  // assessor has said fall arrest / a harness is used on this task.
  const rescueRequired = (methodDef?.rescueMandatory ?? false) || harnessUsed;

  // Reset the method checklist when the method changes.
  const onMethodChange = (m: AccessMethod | "") => {
    setMethod(m);
    setMethodChecks(emptyChecks());
    setLadderJustification("");
  };

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
    id: string,
    v: boolean,
  ) => setter((c) => ({ ...c, [id]: v }));

  /* The hierarchy gate short-circuits the assessment: if the work can be
     avoided or done from an existing safe place, the answer is to do that. */
  const gateResolved = avoid === "yes" || safePlace === "yes";

  const assessment = useMemo(() => {
    // Only assess once an access method is chosen and the gate hasn't short-circuited.
    if (gateResolved) {
      return {
        level: "low" as Verdict,
        title: avoid === "yes" ? "Avoid the work at height" : "Use the existing safe place",
        advice:
          avoid === "yes"
            ? "You have identified that the work at height can be avoided. Under the Work at Height Regulations 2005 you must take that option — do the work from the ground (or redesign it) rather than selecting access equipment."
            : "You have identified an existing safe place to work from. Use it in preference to temporary access equipment, and confirm its edge protection and access route are adequate before work starts.",
        gaps: [] as string[],
      };
    }
    if (!methodDef) return null;

    // Collect every safety-critical prompt that applies, and count the unanswered.
    const applicable: { id: string; label: string; checked: boolean }[] = [];
    const push = (list: Prompt[], checks: Record<string, boolean>, prefix: string) =>
      list.forEach((p) => applicable.push({ id: `${prefix}:${p.id}`, label: p.label, checked: !!checks[p.id] }));

    push(methodDef.prompts, methodChecks, "method");
    push(STEP3_COLLECTIVE, protectionChecks, "collective");
    push(STEP3_OBJECTS, protectionChecks, "objects");
    push(STEP3_WEATHER, protectionChecks, "weather");
    if (rescueRequired) push(STEP4_RESCUE, rescueChecks, "rescue");

    const gaps = applicable.filter((a) => !a.checked).map((a) => a.label);
    if (isLadder && ladderJustification.trim().length === 0) {
      gaps.push("Ladder use not yet justified — record why this is short-duration, low-risk work with no reasonable platform alternative");
    }
    if (windThreshold.trim().length === 0) {
      gaps.push("Wind / weather stop-work threshold not set for the equipment in use");
    }

    // Rescue gaps are weighted hardest: any unanswered rescue prompt on
    // harness/MEWP work is a stop. Hierarchy not recorded is also serious.
    const rescueGap = rescueRequired && STEP4_RESCUE.some((p) => !rescueChecks[p.id]);
    const hierarchyGap = avoid === "" || safePlace === "";

    let level: Verdict;
    let title: string;
    let advice: string;

    if (rescueGap) {
      level = "veryhigh";
      title = "Rescue plan incomplete — do not proceed";
      advice =
        "Harness and MEWP work cannot go ahead without a self-sufficient rescue plan (WAHR 2005 Reg. 4(1)(c)). Complete every rescue prompt — who recovers the worker, what kit, the suspension-trauma time limit, and the fact you are not relying on 999 — before this assessment is workable.";
    } else if (gaps.length === 0 && !hierarchyGap) {
      level = "low";
      title = "Controls addressed — ready for competent-person review";
      advice =
        "Every safety-critical prompt for this access method has been addressed. This is a planning aid, not approval to proceed: a competent person must still review the full RAMS and the actual site conditions before work starts.";
    } else if (gaps.length + (hierarchyGap ? 1 : 0) <= 2) {
      level = "medium";
      title = `${gaps.length + (hierarchyGap ? 1 : 0)} safety-critical point${gaps.length + (hierarchyGap ? 1 : 0) === 1 ? "" : "s"} not yet addressed`;
      advice =
        "Close the outstanding points below before the work is planned in. Until they are resolved a competent person should not sign this off.";
    } else {
      level = "high";
      title = `${gaps.length + (hierarchyGap ? 1 : 0)} safety-critical points not yet addressed`;
      advice =
        "Several controls for this access method are unconfirmed. Work through the outstanding points below — a reviewer will reject access plans that leave the hierarchy decision, falling-object controls or rescue arrangements blank.";
    }

    return { level, title, advice, gaps: hierarchyGap ? ["The avoid / safe-place hierarchy decision has not been recorded", ...gaps] : gaps };
  }, [gateResolved, avoid, safePlace, methodDef, methodChecks, protectionChecks, rescueChecks, rescueRequired, isLadder, ladderJustification, windThreshold]);

  // Fire the funnel event once per visit when a method is first selected.
  useEffect(() => {
    if (method && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("working-at-height-assessment", { method });
    }
  }, [method]);

  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  // Core fields the record should not leave the site without. When the
  // hierarchy gate is satisfied (avoid / safe place) the method-specific items
  // drop away; otherwise they apply.
  const hierarchyRecorded = avoid !== "" && safePlace !== "";
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Task / work at height", done: task.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
    { label: "Hierarchy decision (avoid / safe place) recorded", done: hierarchyRecorded },
  ];
  if (avoid === "no" && safePlace === "no") {
    requiredItems.push({ label: "Justification for using access equipment", done: hierarchyJustification.trim().length > 0 });
  }
  if (!gateResolved) {
    requiredItems.push({ label: "Access method chosen", done: method !== "" });
    if (isLadder) {
      requiredItems.push({ label: "Ladder short-duration, low-risk justification", done: ladderJustification.trim().length > 0 });
    }
    requiredItems.push({ label: "Wind / weather stop-work threshold", done: windThreshold.trim().length > 0 });
    if (rescueRequired) {
      requiredItems.push({ label: "Rescue plan complete (harness / MEWP work)", done: STEP4_RESCUE.every((p) => !!rescueChecks[p.id]) });
    }
  }
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* ── Inputs ─────────────────────────────────────────────── */}
      <div className="space-y-6">
        {/* Header */}
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">The task</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Task / work at height" className="sm:col-span-2">
              <TextInput value={task} onChange={(e) => setTask(e.target.value)} placeholder="e.g. Replace guttering to rear elevation" />
            </Field>
            <Field label="Location / site">
              <TextInput value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. 14 Mill Lane, Unit 3" />
            </Field>
            <Field label="Duration of work" hint="Short duration is roughly under 30 min in one position.">
              <TextInput value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 2 hours" />
            </Field>
            <Field label="Frequency">
              <TextInput value={frequency} onChange={(e) => setFrequency(e.target.value)} placeholder="e.g. one-off / weekly" />
            </Field>
          </div>
        </div>

        {/* Step 1 — hierarchy gate */}
        <div className="card shadow-soft p-6">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 1 — avoid it first</div>
          <h2 className="mt-1 text-xl">The Work at Height hierarchy</h2>
          <p className="mt-1 text-sm text-muted">
            WAHR 2005 requires you to work through the hierarchy in order: <strong>avoid</strong> the work at height, then{" "}
            <strong>prevent</strong> falls, then <strong>minimise</strong> them. Answer these before picking access equipment.
          </p>
          <div className="mt-4 space-y-4">
            {STEP1.map((p, i) => {
              const value = i === 0 ? avoid : safePlace;
              const setValue = i === 0 ? setAvoid : setSafePlace;
              return (
                <Field key={p.id} label={p.label}>
                  <SelectInput value={value} onChange={(e) => setValue(e.target.value as "" | "yes" | "no")}>
                    <option value="">Select…</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </SelectInput>
                </Field>
              );
            })}
          </div>
          {gateResolved && (
            <div className="mt-4 rounded-xl border border-risk-low/40 bg-emerald-tint px-4 py-3 text-[0.9rem] text-emerald-deep">
              {avoid === "yes"
                ? "You can avoid the work at height — do that rather than selecting access equipment. No access method needs to be assessed."
                : "You can work from an existing safe place — use it in preference to temporary access. Confirm its edge protection and access route are adequate."}
            </div>
          )}
          {avoid === "no" && safePlace === "no" && (
            <Field
              label="Why can the work not be avoided or done from a safe place?"
              hint="Record the reasoning for dropping to access equipment / fall protection — this is the justification a reviewer looks for under WAHR 2005."
              className="mt-4"
            >
              <TextArea
                value={hierarchyJustification}
                onChange={(e) => setHierarchyJustification(e.target.value)}
                placeholder="e.g. One-off gutter repair at 3 m; no permanent platform and ground-level assembly not possible; tower selected as the lowest-risk practicable option."
              />
            </Field>
          )}
        </div>

        {/* Step 2 — access method */}
        {!gateResolved && (
          <div className="card shadow-soft p-6">
            <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 2 — access method</div>
            <h2 className="mt-1 text-xl">Choose the access method</h2>
            <p className="mt-1 text-sm text-muted">Pick the method you intend to use, then confirm the controls specific to it.</p>
            <Field label="Access method" className="mt-4">
              <SelectInput value={method} onChange={(e) => onMethodChange(e.target.value as AccessMethod | "")}>
                <option value="">Select an access method…</option>
                {(Object.keys(METHODS) as AccessMethod[]).map((m) => (
                  <option key={m} value={m}>{METHODS[m].label}</option>
                ))}
              </SelectInput>
            </Field>

            {methodDef && (
              <div className="mt-5">
                <div className="mb-2 text-[0.85rem] font-semibold text-ink">{methodDef.label} — controls to confirm</div>
                <div className="space-y-2">
                  {methodDef.prompts.map((p) => (
                    <CheckboxRow key={p.id} checked={!!methodChecks[p.id]} onChange={(v) => toggle(setMethodChecks, p.id, v)}>
                      {p.label}
                    </CheckboxRow>
                  ))}
                </div>
              </div>
            )}

            {isLadder && (
              <Field
                label="Ladder justification — short duration, low risk"
                hint="Ladders are not a default. WAHR 2005 expects a recorded reason: why is this short-duration (about 30 min in one position), low-risk work with no reasonable platform alternative?"
                className="mt-5"
              >
                <TextArea
                  value={ladderJustification}
                  onChange={(e) => setLadderJustification(e.target.value)}
                  placeholder="e.g. 10-minute light fitting swap at 2.5 m; can maintain three points of contact; no side-loading; tower would take longer to erect than the task."
                />
              </Field>
            )}
          </div>
        )}

        {/* Step 3 — protection */}
        {!gateResolved && methodDef && (
          <div className="card shadow-soft p-6">
            <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 3 — protection</div>
            <h2 className="mt-1 text-xl">Collective before personal; protect those below</h2>

            <div className="mt-4">
              <div className="mb-2 text-[0.85rem] font-semibold text-ink">Fall protection — guardrails before harnesses</div>
              <div className="space-y-2">
                {STEP3_COLLECTIVE.map((p) => (
                  <CheckboxRow key={p.id} checked={!!protectionChecks[p.id]} onChange={(v) => toggle(setProtectionChecks, p.id, v)}>
                    {p.label}
                  </CheckboxRow>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-2 text-[0.85rem] font-semibold text-ink">Falling-objects controls</div>
              <div className="space-y-2">
                {STEP3_OBJECTS.map((p) => (
                  <CheckboxRow key={p.id} checked={!!protectionChecks[p.id]} onChange={(v) => toggle(setProtectionChecks, p.id, v)}>
                    {p.label}
                  </CheckboxRow>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-2 text-[0.85rem] font-semibold text-ink">Weather limits</div>
              <div className="space-y-2">
                {STEP3_WEATHER.map((p) => (
                  <CheckboxRow key={p.id} checked={!!protectionChecks[p.id]} onChange={(v) => toggle(setProtectionChecks, p.id, v)}>
                    {p.label}
                  </CheckboxRow>
                ))}
              </div>
              <Field
                label="Wind / weather stop-work threshold"
                hint="State the wind speed (or conditions) at which work stops for the equipment in use — e.g. MEWP/tower manufacturers commonly set a limit around 17 m/s. Monitor the forecast against it."
                className="mt-3"
              >
                <TextInput
                  value={windThreshold}
                  onChange={(e) => setWindThreshold(e.target.value)}
                  placeholder="e.g. Stop at 17 m/s (Beaufort 7) per the tower manual; also stop in ice, lightning or heavy rain"
                />
              </Field>
            </div>
          </div>
        )}

        {/* Step 4 — rescue plan */}
        {!gateResolved && methodDef && (
          <div className="card shadow-soft p-6">
            <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 4 — rescue plan</div>
            <h2 className="mt-1 text-xl">Rescue plan{rescueRequired && <span className="text-risk-veryhigh"> — mandatory</span>}</h2>
            {!methodDef.rescueMandatory && (
              <div className="mt-3 rounded-lg border border-line bg-mist/60 p-4">
                <CheckboxRow checked={harnessUsed} onChange={setHarnessUsed}>
                  Personal fall arrest / a harness is relied on for this task (e.g. harness-based roof work, restraint lanyard)
                </CheckboxRow>
                <p className="mt-2 text-[0.82rem] leading-relaxed text-muted">
                  Tick this if anyone wears a harness for fall protection — a self-sufficient rescue plan then becomes
                  mandatory, because a suspended worker can suffer suspension trauma within minutes.
                </p>
              </div>
            )}
            <p className="mt-3 text-sm text-muted">
              {rescueRequired
                ? "Harness and MEWP work cannot proceed without a self-sufficient rescue plan. Every point below must be addressed."
                : "If any harness or fall-arrest equipment is used on this task, a rescue plan is required. Tick the box above and complete it where relevant."}
            </p>
            <div className="mt-4 space-y-2">
              {STEP4_RESCUE.map((p) => (
                <CheckboxRow key={p.id} checked={!!rescueChecks[p.id]} onChange={(v) => toggle(setRescueChecks, p.id, v)}>
                  {p.label}
                </CheckboxRow>
              ))}
            </div>
            <Field label="Rescue arrangements — who, what kit, how" className="mt-4">
              <TextInput
                value={rescueNotes}
                onChange={(e) => setRescueNotes(e.target.value)}
                placeholder="e.g. Ground person (J. Smith) trained on MEWP auxiliary lowering; recovery within 5 min"
              />
            </Field>
          </div>
        )}

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* ── Result + printable record ──────────────────────────── */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Assessment completion" />
        </div>
        {assessment ? (
          <>
            <div className="print-area card shadow-soft p-6" id="wah-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Work at height assessment
              </div>
              <h2 className="mt-1 text-lg">{task || "Untitled task"}</h2>
              <div className="mt-1 text-[0.82rem] text-muted">
                {[location && location, evidence.preparedBy && `Assessor: ${evidence.preparedBy}`, today].filter(Boolean).join(" · ")}
              </div>
              {(duration || frequency) && (
                <div className="text-[0.82rem] text-muted">
                  {[duration && `Duration: ${duration}`, frequency && `Frequency: ${frequency}`].filter(Boolean).join(" · ")}
                </div>
              )}

              <div className="mt-4">
                <ResultBanner level={assessment.level} title={assessment.title}>
                  {assessment.advice}
                </ResultBanner>
              </div>

              {/* Step 1 outcome */}
              <div className="mt-4">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">Step 1 — hierarchy</div>
                <ul className="mt-1 space-y-0.5 text-[0.86rem] text-body">
                  <li>Can the work at height be avoided? <strong>{avoid ? (avoid === "yes" ? "Yes" : "No") : "—"}</strong></li>
                  <li>Can it be done from an existing safe place? <strong>{safePlace ? (safePlace === "yes" ? "Yes" : "No") : "—"}</strong></li>
                </ul>
                {avoid === "no" && safePlace === "no" && (
                  <p className="mt-1.5 text-[0.86rem] text-body">
                    <strong>Justification:</strong> {hierarchyJustification || "—"}
                  </p>
                )}
              </div>

              {!gateResolved && methodDef && (
                <>
                  {/* Step 2 — method */}
                  <div className="mt-4">
                    <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">Step 2 — access method</div>
                    <div className="mt-1 text-[0.86rem] text-body">{methodDef.label}</div>
                    <ul className="mt-1 space-y-0.5 text-[0.86rem]">
                      {methodDef.prompts.map((p) => (
                        <li key={p.id} className={methodChecks[p.id] ? "text-body" : "text-risk-veryhigh"}>
                          {methodChecks[p.id] ? "✓" : "✗"} {p.label}
                        </li>
                      ))}
                    </ul>
                    {isLadder && (
                      <p className="mt-1.5 text-[0.86rem] text-body">
                        <strong>Ladder justification:</strong> {ladderJustification || "—"}
                      </p>
                    )}
                  </div>

                  {/* Step 3 — protection */}
                  <div className="mt-4">
                    <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">Step 3 — protection</div>
                    <ul className="mt-1 space-y-0.5 text-[0.86rem]">
                      {[...STEP3_COLLECTIVE, ...STEP3_OBJECTS, ...STEP3_WEATHER].map((p) => (
                        <li key={p.id} className={protectionChecks[p.id] ? "text-body" : "text-risk-veryhigh"}>
                          {protectionChecks[p.id] ? "✓" : "✗"} {p.label}
                        </li>
                      ))}
                    </ul>
                    <p className={`mt-1.5 text-[0.86rem] ${windThreshold ? "text-body" : "text-risk-veryhigh"}`}>
                      <strong>Wind / weather stop limit:</strong> {windThreshold || "not set"}
                    </p>
                  </div>

                  {/* Step 4 — rescue */}
                  <div className="mt-4">
                    <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                      Step 4 — rescue plan{rescueRequired ? " (mandatory)" : ""}
                    </div>
                    {harnessUsed && !methodDef.rescueMandatory && (
                      <p className="mt-1 text-[0.86rem] text-body">Fall arrest / harness relied on for this task.</p>
                    )}
                    <ul className="mt-1 space-y-0.5 text-[0.86rem]">
                      {STEP4_RESCUE.map((p) => (
                        <li key={p.id} className={rescueChecks[p.id] ? "text-body" : "text-risk-veryhigh"}>
                          {rescueChecks[p.id] ? "✓" : "✗"} {p.label}
                        </li>
                      ))}
                    </ul>
                    {rescueNotes && <p className="mt-1.5 text-[0.86rem] text-body"><strong>Arrangements:</strong> {rescueNotes}</p>}
                  </div>
                </>
              )}

              <EvidencePrint value={evidence} briefing />

              <PrintFooter toolName="work at height assessment" />
            </div>
            <GuardedPrintButton
              tool="working-at-height-assessment"
              label="Print / save record as PDF"
              missing={missingRequired}
              warningTitle="This work at height assessment has missing safety-critical fields"
            />
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the Step 1 hierarchy questions and pick an access method to build a printable work at height
            assessment record.
          </div>
        )}
      </div>
    </div>
  );
}
